import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Rocket, LogIn, Briefcase, Users, Video, Calendar, Command, Bell,
  Award, ShieldCheck, ArrowRight, Sparkles, CheckCircle2, PlayCircle,
  BookOpen, Mail, MessageSquare, Zap,
} from "lucide-react";

const DEMO_ACCOUNTS = [
  { role: "Super Admin", email: "abi@yousolved.com", scope: "Everything" },
  { role: "Recruiter", email: "recruiter@alfredaihire.com", scope: "Jobs, pipeline, interviews, references" },
  { role: "Hiring Manager", email: "manager@alfredaihire.com", scope: "Review candidates, score interviews" },
  { role: "Employee", email: "employee@alfredaihire.com", scope: "Personal onboarding, documents, streak" },
  { role: "Candidate", email: "candidate@alfredaihire.com", scope: "Video interview, prep pack, portal" },
];

const STEPS = [
  {
    n: 1, Icon: LogIn, title: "Sign in with a demo account",
    body: "Pick a role below (recruiter is the best place to start). Every account uses the same password.",
    cta: { label: "Open sign in", to: "/login" },
    highlight: "Password: Alfred!2026",
  },
  {
    n: 2, Icon: Briefcase, title: "Open a job",
    body: "Jobs live in Recruit → Jobs. Each role has a Kanban pipeline (Invited → In Progress → Submitted → Evaluated → Offered → Hired) with drag-and-drop.",
    cta: { label: "Go to Jobs", to: "/app/recruit/jobs" },
    tip: "Tap Auto-fix on any flagged job — Alfred rewrites the JD in three modes and picks the strongest one for you.",
  },
  {
    n: 3, Icon: Users, title: "Move a candidate through the pipeline",
    body: "Open any candidate to see their video interview, AI scorecard, references, and behavioural profile. Drag between stages, or say 'move Alex to Offered' in voice.",
    tip: "Move a candidate to HIRED and Alfred auto-provisions their onboarding checklist.",
  },
  {
    n: 4, Icon: Video, title: "Watch an interview with AI chapters",
    body: "Every submitted video has per-question chapters, transcript, sentiment markers and an AI-generated summary card. Skim in seconds — no full replay needed.",
  },
  {
    n: 5, Icon: Calendar, title: "Schedule from the calendar",
    body: "Switch between Month / Week / Day / List / Grid. Drag any event to reschedule — the interview time updates instantly and re-notifies both sides.",
    cta: { label: "Open Calendar", to: "/app/calendar" },
  },
  {
    n: 6, Icon: Command, title: "Command Palette (Ctrl + K)",
    body: "Global navigation, actions and slash-commands from anywhere. Type `>` for actions (>create-job), `/` for settings (/theme dark), or just start typing to search jobs, candidates and pages.",
    tip: "Try: `>generate-jd Senior Engineer` or `/notify test`",
  },
  {
    n: 7, Icon: Bell, title: "Notifications & reference reminders",
    body: "Bell popover surfaces everything actionable. References auto-nudge every 48h up to 3 times — and referees can give a 10-second one-tap recommendation instead of the long form.",
  },
  {
    n: 8, Icon: Award, title: "Compliance + Evidence Pack",
    body: "The Compliance Hub tracks ISO 27001, 42001 and 45001 with live health scoring. One click generates a Vanta-style PDF audit pack — signatures, certifications, monitors and audit trail all embedded.",
    cta: { label: "Open Compliance", to: "/app/compliance" },
  },
];

const AI_FEATURES = [
  { Icon: Sparkles, title: "JD Auto-Generation", body: "Claude Sonnet 5 drafts an inclusive, bias-free job description in seconds." },
  { Icon: PlayCircle, title: "Video Chapters + Summary", body: "Per-question chapters, transcript search, and a 3-bullet AI summary of every candidate." },
  { Icon: Users, title: "Big Five Behavioural Profile", body: "Predictive scoring across Openness, Conscientiousness, Extraversion, Agreeableness, Emotional stability." },
  { Icon: Zap, title: "Talent Match", body: "Natural-language search across your talent pool: 'React engineers who've worked at seed-stage startups'." },
  { Icon: MessageSquare, title: "Panel Debrief", body: "Auto-merges every interviewer's AI notes into a single hire/no-hire recommendation with rationale." },
  { Icon: ShieldCheck, title: "One-Tap References", body: "Referees give a 10-second Absolutely/Yes/Maybe/No recommendation. Alfred writes the summary for you." },
];

export default function QuickstartPage() {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="hero-gradient relative overflow-hidden">
        <div className="grain" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <Link to="/" data-testid="quickstart-home-link" className="inline-flex items-center gap-3 text-white/80 hover:text-white text-sm mb-8">
            <img src="/alfred-ai-white.png" alt="alfred ai" className="h-7 w-auto" />
            <span className="text-white/40">/</span>
            <span>alfredHR quickstart</span>
          </Link>
          <Badge className="bg-white/10 text-white border-0 rounded-full mb-4">
            <BookOpen className="w-3 h-3 mr-1.5" /> 5-minute read
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight" data-testid="quickstart-title">
            Get productive on <span className="text-pink">alfredHR</span> in ten minutes.
          </h1>
          <p className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">
            This is the fastest way to explore every module — recruit, interviews, calendar, onboarding, compliance and the AI features that tie them together. Pick a demo role, follow the eight steps, and you're set.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login" data-testid="quickstart-signin-btn">
              <Button className="btn-primary rounded-full px-7 py-6 text-base">
                Sign in to try <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="https://new.alfredai.bot/book" target="_blank" rel="noopener noreferrer" data-testid="quickstart-book-btn">
              <Button variant="ghost" className="text-white hover:bg-white/10 rounded-full px-7 py-6 text-base">
                Book a walkthrough
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Demo accounts card */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 -mt-10 relative z-10">
        <Card className="border-0 shadow-xl" data-testid="quickstart-accounts-card">
          <CardContent className="p-6 lg:p-8">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-pink font-semibold">Demo accounts</div>
                <h2 className="text-2xl font-bold font-display mt-1">Pick a role and log in</h2>
                <p className="text-sm text-neutral-600 mt-1">Every account uses the same password: <code className="bg-neutral-100 px-2 py-0.5 rounded font-mono text-pink">Alfred!2026</code></p>
              </div>
              <Link to="/login" data-testid="quickstart-inline-signin">
                <Button className="btn-primary rounded-full">Open login</Button>
              </Link>
            </div>
            <div className="mt-6 divide-y">
              {DEMO_ACCOUNTS.map((a) => (
                <div key={a.email} className="py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center" data-testid={`quickstart-account-${a.role.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="md:col-span-3 text-sm font-semibold text-navy">{a.role}</div>
                  <div className="md:col-span-4 font-mono text-xs text-neutral-700">{a.email}</div>
                  <div className="md:col-span-5 text-xs text-neutral-500">{a.scope}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 8 steps */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-pink font-semibold">The tour</div>
          <h2 className="text-3xl font-bold font-display mt-1">Eight steps to see the whole platform</h2>
        </div>
        <div className="space-y-4">
          {STEPS.map((s) => (
            <Card key={s.n} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`quickstart-step-${s.n}`}>
              <CardContent className="p-6 lg:p-8">
                <div className="grid grid-cols-12 gap-6 items-start">
                  <div className="col-span-2 md:col-span-1">
                    <div className="w-10 h-10 rounded-full bg-pink/10 text-pink grid place-items-center font-bold font-mono">
                      {String(s.n).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <div className="flex items-center gap-2">
                      <s.Icon className="w-5 h-5 text-navy" />
                      <h3 className="text-xl font-bold font-display">{s.title}</h3>
                    </div>
                    <p className="text-neutral-700 mt-2 leading-relaxed">{s.body}</p>
                    {s.highlight && (
                      <div className="mt-3 text-xs bg-navy/5 border border-navy/10 rounded-lg px-3 py-2 inline-block text-navy">
                        {s.highlight}
                      </div>
                    )}
                    {s.tip && (
                      <div className="mt-3 flex items-start gap-2 text-sm text-neutral-600 bg-amber-50 border border-amber-100 rounded-lg p-3">
                        <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <span><strong className="text-amber-800">Tip:</strong> {s.tip}</span>
                      </div>
                    )}
                    {s.cta && (
                      <div className="mt-4">
                        <Link to={s.cta.to} data-testid={`quickstart-cta-step-${s.n}`}>
                          <Button variant="outline" className="rounded-full">
                            {s.cta.label} <ArrowRight className="ml-2 w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* AI features grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-pink font-semibold">Under the hood</div>
          <h2 className="text-3xl font-bold font-display mt-1">The AI you actually notice</h2>
          <p className="text-neutral-600 mt-2 max-w-2xl">Every AI feature is Claude Sonnet 5 or Whisper via Emergent Universal Key. No configuration required — it just works.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AI_FEATURES.map((f) => (
            <Card key={f.title} className="border-0 shadow-sm" data-testid={`quickstart-ai-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-pink/10 text-pink grid place-items-center mb-4">
                  <f.Icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold font-display text-lg">{f.title}</h3>
                <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{f.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Cheat sheet */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="quickstart-cheatsheet">
        <Card className="border-0 shadow-sm bg-navy text-white overflow-hidden">
          <CardContent className="p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <div className="text-xs uppercase tracking-widest text-pink font-semibold">Cheat sheet</div>
                <h2 className="text-3xl font-bold font-display mt-1">Keyboard shortcuts</h2>
                <p className="text-white/70 mt-2 max-w-md">Alfred is built for keyboard flow. These four shortcuts cover 80% of daily use.</p>
              </div>
              <div className="space-y-3 text-sm">
                {[
                  { keys: "Ctrl + K", label: "Open Command Palette" },
                  { keys: "> action", label: "Actions (>create-job, >generate-jd)" },
                  { keys: "/ setting", label: "Settings (/theme dark, /notify)" },
                  { keys: "Esc", label: "Close any open panel" },
                ].map((s) => (
                  <div key={s.keys} className="flex items-center justify-between border-b border-white/10 py-2">
                    <kbd className="font-mono text-xs bg-white/10 text-white px-3 py-1.5 rounded-lg">{s.keys}</kbd>
                    <span className="text-white/80">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* CTA row */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8">
              <Rocket className="w-6 h-6 text-pink" />
              <h3 className="text-xl font-bold font-display mt-3">Ready to try it?</h3>
              <p className="text-sm text-neutral-600 mt-2 mb-4">Log in with the recruiter account — it's where 90% of the magic happens.</p>
              <Link to="/login" data-testid="quickstart-cta-login-bottom">
                <Button className="btn-primary rounded-full">
                  Sign in <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8">
              <Mail className="w-6 h-6 text-pink" />
              <h3 className="text-xl font-bold font-display mt-3">Want a walkthrough?</h3>
              <p className="text-sm text-neutral-600 mt-2 mb-4">Book a 20-minute call with a founder — we'll tailor the demo to your team.</p>
              <a href="https://new.alfredai.bot/book" target="_blank" rel="noopener noreferrer" data-testid="quickstart-cta-book-bottom">
                <Button variant="outline" className="rounded-full">
                  Book a call <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-neutral-400">
          <CheckCircle2 className="w-3.5 h-3.5 text-teal" /> ISO 27001 · GDPR · SOC 2 Type II · ISO 42001 · ISO 45001 Ready
        </div>
      </section>
    </div>
  );
}
